import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Upload, Image, X, Clipboard } from 'lucide-react';

interface ImageDropzoneProps {
  onImageDrop: (file: File) => void;
  className?: string;
}

const ImageDropzone: React.FC<ImageDropzoneProps> = ({ 
  onImageDrop,
  className = '',
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPasteHint, setShowPasteHint] = useState(false);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  // Check if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const processFile = useCallback((file: File) => {
    setError(null);
    
    // Check if file is an image
    if (!file.type.match('image.*')) {
      setError('Please select an image file');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image is too large. Please select an image under 10MB.');
      return;
    }

    // Create a preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setPreviewImage(e.target.result as string);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file. Please try again.');
    };
    reader.readAsDataURL(file);
    
    setSelectedFile(file);
    onImageDrop(file);
  }, [onImageDrop]);

  // Handle clipboard paste
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    e.preventDefault();
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          processFile(file);
          setShowPasteHint(false);
          return;
        }
      }
    }
    
    // If no image was found in clipboard
    setError('No image found in clipboard. Please copy an image first.');
  }, [processFile]);

  // Add event listeners for clipboard paste
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        setShowPasteHint(true);
        setTimeout(() => setShowPasteHint(false), 2000);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  }, [processFile]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  }, [processFile]);

  const clearSelectedImage = useCallback(() => {
    setPreviewImage(null);
    setSelectedFile(null);
    setError(null);
    
    // Clear the file input value so the same file can be selected again
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }, []);

  const handlePasteClick = useCallback(async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/')) {
            const blob = await clipboardItem.getType(type);
            const file = new File([blob], 'pasted-image.png', { type });
            processFile(file);
            return;
          }
        }
      }
      
      setError('No image found in clipboard. Please copy an image first.');
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      setError('Unable to access clipboard. Please use drag & drop or file selection.');
    }
  }, [processFile]);

  return (
    <div className={`${className}`}>
      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-3 text-sm">
          {error}
        </div>
      )}
      
      {showPasteHint && (
        <div className="bg-blue-50 text-blue-600 p-3 rounded-lg mb-3 text-sm flex items-center gap-2">
          <Clipboard className="w-4 h-4" />
          Press Ctrl+V (or Cmd+V) to paste an image from your clipboard
        </div>
      )}
      
      {previewImage ? (
        <div className="relative">
          <div className="relative aspect-[4/3] max-w-xs mx-auto overflow-hidden rounded-lg border-2 border-gray-200">
            <img 
              src={previewImage} 
              alt="Preview" 
              className="w-full h-full object-cover"
            />
            <button 
              onClick={clearSelectedImage}
              className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-center mt-2 text-gray-600">
            {selectedFile?.name} ({Math.round(selectedFile?.size ? selectedFile.size / 1024 : 0)} KB)
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div
            ref={dropzoneRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center transition-colors cursor-pointer aspect-[4/3]
              ${isDragActive ? 'border-gray-700 bg-gray-100' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}
            `}
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <div className="mb-4 p-3 bg-gray-200 rounded-full text-gray-700">
              <Image className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-gray-700">
              {isMobile ? 'Tap to select from photo library' : 'Drag and drop your house image here'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {isMobile ? 'or take a new photo' : 'or click to browse'}
            </p>
            <p className="text-xs text-gray-400 mt-3">
              Supported formats: JPEG, PNG
            </p>
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              multiple={false}
            />
          </div>
          
          {/* Paste button for desktop */}
          {!isMobile && (
            <button
              onClick={handlePasteClick}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
            >
              <Clipboard className="w-4 h-4" />
              Paste from clipboard
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageDropzone; 